import * as React from "react";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { TemplateField } from "@/shared/components/TemplateField";
import { goalTypeLabel } from "@/shared/domain/agents";
import { Plus } from "@/shared/foundation/icons";
import {
  Banner,
  Field,
  FormBlock,
  FormSection,
  RobotoButton,
} from "@/shared/foundation/primitives";
import { agentFormSectionId } from "@/shared/navigation";
import { pluralize } from "@/shared/text";

import {
  MAX_GOALS,
  newGoalValues,
  type AgentFormValues,
} from "../agentFormSchema";
import { GoalCard } from "../goals/GoalCard";
import { DEFAULT_GOAL_KIND } from "../goals/goalKinds";
import cards from "../goals/goals.module.css";
import { useAgentVariableCatalog } from "../useAgentVariableCatalog";
import { VariablesBlock } from "../VariablesBlock";

import styles from "./sections.module.css";

/**
 * What the agent is for: the goals it must finish, whatever else it should be
 * told before its first turn, and the variables both of those declare.
 *
 * Goals are independent of one another, so the list keeps insertion order and
 * offers no reordering.
 */
export function GoalsSection() {
  const { control, getFieldState, formState } =
    useFormContext<AgentFormValues>();
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "goals",
  });
  const catalog = useAgentVariableCatalog();
  const kickoffTitleId = React.useId();
  const listError = getFieldState("goals", formState).error;
  const passthroughGoals = useWatch({ control, name: "passthroughGoals" });
  const keptGoalNames = passthroughGoals.map(({ goal }) =>
    goalTypeLabel(String(goal.goal_type)),
  );
  const goalCount = fields.length + keptGoalNames.length;

  return (
    <FormSection
      title="What goals the agent must accomplish"
      titleId={agentFormSectionId("goals")}
      hint="A goal is work the agent has to finish before its turn is done."
      actions={
        <span className={styles.count} data-testid="agent-goals-count">
          {goalCount} {pluralize("goal", goalCount)}
        </span>
      }
    >
      <div className={styles.groups}>
        {keptGoalNames.length > 0 && (
          <Banner
            variant="info"
            title={`Set through the SDK or API: ${keptGoalNames.join(", ")}`}
            description="The editor cannot change a goal set this way. Saving the agent keeps it as it is."
            data-testid="agent-kept-goals"
          />
        )}
        <div className={cards.goals}>
          {fields.length > 0 && (
            <div
              className={cards.list}
              data-testid="agent-goals"
              data-count={fields.length}
            >
              {fields.map((field, index) => (
                <GoalCard
                  key={field.id}
                  index={index}
                  onRemove={() => {
                    remove(index);
                  }}
                  onChangeKind={(kind) => {
                    update(index, newGoalValues(kind));
                  }}
                />
              ))}
            </div>
          )}

          <div>
            <RobotoButton
              variant="secondary"
              icon={<Plus aria-hidden size={16} />}
              disabled={goalCount >= MAX_GOALS}
              onClick={() => {
                append(newGoalValues(DEFAULT_GOAL_KIND));
              }}
              data-testid="agent-add-goal"
            >
              Add goal
            </RobotoButton>
          </div>

          {listError?.message !== undefined && (
            <Field.Root invalid>
              <Field.Error match>{listError.message}</Field.Error>
            </Field.Root>
          )}
        </div>

        <FormBlock
          titleId={kickoffTitleId}
          title="Kickoff message"
          hint="Context for the agent's first turn that no goal covers. Required when there are no goals."
          data-testid="agent-kickoff"
        >
          <Controller
            control={control}
            name="kickoffMessage"
            render={({ field, fieldState }) => (
              <Field.Root
                invalid={fieldState.invalid}
                data-testid="agent-kickoff-field"
              >
                <TemplateField
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  catalog={catalog}
                  labelledBy={kickoffTitleId}
                  rows={4}
                  error={fieldState.invalid}
                  placeholder="Focus the triage on the {{customer}} workspace this week."
                />
                <Field.Error match>{fieldState.error?.message}</Field.Error>
              </Field.Root>
            )}
          />
        </FormBlock>

        <VariablesBlock />
      </div>
    </FormSection>
  );
}
